import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowLeft, Download, ShieldCheck, Calendar, Briefcase, Award } from "lucide-react";
import axios from "axios";
import { API } from "../../lib/api";

export default function VerifyResult() {
    const { certificateId } = useParams();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const verify = async () => {
            try {
                const r = await axios.get(`${API}/verify/${certificateId}`);
                setResult(r.data);
            } catch (e) {
                setError(e.response?.data?.detail || "Document not found or invalid ID");
            } finally {
                setLoading(false);
            }
        };
        verify();
    }, [certificateId]);

    const handleDownload = () => {
        window.open(`${API}/verify/download/${certificateId}`, "_blank");
    };

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            {/* Background decorations */}
            <div className="absolute top-0 left-0 right-0 h-96 bg-gradient-to-b from-primary/10 to-transparent pointer-events-none" />
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[550px] h-[550px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />

            <div className="sm:mx-auto sm:w-full sm:max-w-xl relative z-10">
                <Link to="/verify" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition mb-6">
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Verification Portal</span>
                </Link>

                {loading ? (
                    <div className="bg-slate-800/80 backdrop-blur-xl border border-slate-700 py-16 px-4 shadow-2xl rounded-3xl text-center">
                        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                        <p className="mt-4 text-sm text-slate-400">Verifying document registry...</p>
                    </div>
                ) : error ? (
                    <div className="bg-slate-800/80 backdrop-blur-xl border border-slate-700 p-8 shadow-2xl rounded-3xl text-center space-y-4">
                        <XCircle className="w-16 h-16 text-rose-500 mx-auto" />
                        <h2 className="text-xl font-bold text-white">Verification Failed</h2>
                        <p className="text-slate-400 text-sm">{error}</p>
                        <div className="pt-4">
                            <Link to="/verify" className="inline-block bg-slate-700 hover:bg-slate-600 text-white font-semibold text-sm px-6 py-2.5 rounded-xl transition">
                                Try Another ID
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="bg-slate-800/80 backdrop-blur-xl border border-slate-700 shadow-2xl rounded-3xl overflow-hidden">
                        {/* Status Header */}
                        <div className={`p-6 text-center border-b border-slate-700/50 flex flex-col items-center gap-3
                            ${result.verified ? "bg-emerald-500/10" : "bg-rose-500/10"}`}
                        >
                            {result.verified ? (
                                <>
                                    <CheckCircle2 className="w-16 h-16 text-emerald-400" />
                                    <div>
                                        <h2 className="text-2xl font-bold text-emerald-400">Document Verified ✅</h2>
                                        <p className="text-xs text-slate-400 mt-1">This document is authentic and registered in our database.</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <XCircle className="w-16 h-16 text-rose-400" />
                                    <div>
                                        <h2 className="text-2xl font-bold text-rose-400">Document Revoked ❌</h2>
                                        <p className="text-xs text-slate-400 mt-1">This certificate has been revoked by administration.</p>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Document Meta Body */}
                        <div className="p-8 space-y-6 text-slate-300">
                            <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                                <div>
                                    <span className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Document Recipient</span>
                                    <span className="text-base font-bold text-white mt-0.5 block">{result.fullName}</span>
                                </div>
                                <div>
                                    <span className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Document Type</span>
                                    <span className="text-base font-bold text-white mt-0.5 block capitalize">{result.documentType?.replace("_", " ")}</span>
                                </div>

                                <div className="col-span-2 border-t border-slate-700/50 pt-4" />

                                <div className="flex items-start gap-2.5">
                                    <Briefcase className="w-4 h-4 text-slate-500 mt-1 shrink-0" />
                                    <div>
                                        <span className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Assigned Role</span>
                                        <span className="text-sm font-semibold text-slate-200 mt-0.5 block">{result.role}</span>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <Calendar className="w-4 h-4 text-slate-500 mt-1 shrink-0" />
                                    <div>
                                        <span className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Duration / Period</span>
                                        <span className="text-sm font-semibold text-slate-200 mt-0.5 block">{result.duration}</span>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <Award className="w-4 h-4 text-slate-500 mt-1 shrink-0" />
                                    <div>
                                        <span className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Certificate ID</span>
                                        <span className="text-sm font-mono text-primary font-bold mt-0.5 block">{result.certificateId}</span>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <ShieldCheck className="w-4 h-4 text-slate-500 mt-1 shrink-0" />
                                    <div>
                                        <span className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Issue Date</span>
                                        <span className="text-sm font-semibold text-slate-200 mt-0.5 block">{result.issueDate}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Additional validation footer */}
                            <div className="bg-slate-900/60 p-4 rounded-2xl border border-slate-700/40 text-xs text-slate-400 space-y-1.5">
                                <div><b>Issuer:</b> {result.issuedBy}</div>
                                <div><b>Current Status:</b> <span className={result.verified ? "text-emerald-400 font-semibold" : "text-rose-400 font-semibold"}>{result.status?.toUpperCase()}</span></div>
                            </div>

                            {/* Download Action if Verified */}
                            {result.verified && (
                                <div className="pt-4">
                                    <button
                                        onClick={handleDownload}
                                        className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white py-3 rounded-xl font-semibold text-sm shadow-md transition"
                                    >
                                        <Download className="w-4 h-4" />
                                        <span>Download Official PDF Copy</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
